import { Stack } from "@fluentui/react";
import React from "react";
import { Heading, HeadingProps } from "../../../../Heading/Heading";
import { Hyperlink, HyperlinkProps } from "../../../../Links/Hyperlink/Hyperlink";
import { Media } from "../../../../Media/Media";
import { MediaProps } from "../../../../Media/Media.types";
import { StopPropagation } from "../../../../StopPropagation/StopPropagation";
import { Text, TextProps } from "../../../../Text/Text";
import { TruncatedText } from "../../../../TruncatedText/TruncatedText";
import { enterKeyStopPropagation } from "../props";
import { leftAlignmentStyles, titleStyles } from "./DrawerHeaderTitle.styles";

export interface DrawerHeaderTitleProps {
    /**
     * Media component to render before the title
     */
    readonly media?: MediaProps;
    /**
     * Text for subtitle in this column
     */
    readonly subtitleTextProps?: DrawerSubtitleProps;
    /**
     * Props for the title used in this column. Can be
     * Text HyperLink or NoHrefLink
     */
    readonly title: DrawerTitleProps;

    /**
     * Id to link with Drawer content
     */
    readonly id?: string;
}

export type DrawerSubtitleProps = Pick<TextProps, "children" | "colorOverride" | "nowrap">;

interface TitleTextProps extends TextProps {
    readonly kind: "text";
}

interface TitleHyperlinkProps extends HyperlinkProps {
    readonly kind: "hyperlink";
}

interface TitleHeadingProps extends HeadingProps {
    readonly kind: "heading";
}

export type DrawerTitleProps = TitleTextProps | TitleHyperlinkProps | TitleHeadingProps;

export const DrawerTitle: React.FC<DrawerTitleProps> = (
    props
) => {
    switch (props.kind) {
        case "text": {
            const { kind, nowrap = true, ...textProps } = props;
            if (nowrap) {
                return <TruncatedText {...textProps} />;
            } else {
                return <Text {...textProps} />;
            }
        }
        case "hyperlink": {
            const { kind, ...hyperlinkProps } = props;
            return (
                <StopPropagation keycodes={[enterKeyStopPropagation]}>
                    <Hyperlink {...hyperlinkProps} />
                </StopPropagation>
            );
        }
        case "heading": {
            const { kind, nowrap = true, ...headingProps } = props;
            if (nowrap) {
                return <TruncatedText type="heading" {...headingProps} />;
            } else {
                return <Heading {...headingProps} />;
            }
        }
    }
};

export const DrawerSubtitle: React.FC<DrawerSubtitleProps> = (
    props
) => {
    const { nowrap = true, ...textProps } = props;
    if (nowrap) {
        return <TruncatedText variant="metadataText" {...textProps} />;
    } else {
        return <Text variant="metadataText" {...textProps} />;
    }
};

export const DrawerHeaderTitle: React.FC<DrawerHeaderTitleProps> = ({
    media,
    title,
    subtitleTextProps,
    id
}) => {
    const titleElement: React.ReactNode = (
        <Stack styles={titleStyles()} verticalAlign="center">
            <Stack horizontal={true} horizontalAlign="start" styles={leftAlignmentStyles(title)}>
                <DrawerTitle {...title} id={id} />
            </Stack>
            <Stack horizontal={true} horizontalAlign="start">
                {subtitleTextProps && <DrawerSubtitle {...subtitleTextProps} />}
            </Stack>
        </Stack>
    );

    return (
        <Stack horizontal={true} verticalAlign="center">
            {media ? (
                <Stack verticalAlign="center">
                    <Media {...media}>
                        {titleElement}
                    </Media>
                </Stack>
            ) : (
                titleElement
            )}
        </Stack>
    );
};
